import React from "react";
import { Document, Page, StyleSheet, Text, View } from "@react-pdf/renderer";

const styles = StyleSheet.create({
  page: {
    padding: 32,
    fontFamily: "Helvetica",
    fontSize: 11,
    color: "#0f172a",
  },
  header: {
    marginBottom: 16,
  },
  title: {
    fontSize: 18,
    fontWeight: 700,
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 12,
    color: "#475569",
  },
  section: {
    marginTop: 16,
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: 700,
    marginBottom: 6,
  },
  row: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 4,
  },
  label: {
    color: "#475569",
  },
  value: {
    fontWeight: 700,
  },
  tag: {
    paddingVertical: 2,
    paddingHorizontal: 6,
    borderRadius: 4,
    backgroundColor: "#e2e8f0",
    fontSize: 9,
    alignSelf: "flex-start",
  },
  grid: {
    display: "flex",
    flexDirection: "row",
    border: "1pt solid #cbd5e1",
    borderRadius: 6,
    overflow: "hidden",
  },
  gridCol: {
    flex: 1,
    borderLeft: "1pt solid #cbd5e1",
  },
  gridHeader: {
    backgroundColor: "#f8fafc",
    paddingVertical: 6,
    paddingHorizontal: 8,
    borderBottom: "1pt solid #cbd5e1",
    fontWeight: 700,
  },
  gridRow: {
    paddingVertical: 6,
    paddingHorizontal: 8,
    borderBottom: "1pt solid #e2e8f0",
  },
  muted: {
    color: "#475569",
    marginTop: 4,
  },
  note: {
    marginTop: 8,
    fontSize: 10,
    color: "#475569",
  },
});

const formatNumber = (n, digits = 3) => {
  if (!Number.isFinite(n)) return "—";
  return n.toFixed(digits);
};

const formatThickness = (n) => {
  if (!Number.isFinite(n)) return "—";
  return `${n} mm`;
};

const formatDateTime = (d) =>
  d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });

export default function UValuePDFDocument({
  project,
  result,
  componentType,
  target,
  compliant,
  airLayer,
  outerLayers,
  innerLayers,
}) {
  const generatedAt = new Date();
  const thicknessMm = Math.round((result?.totalThickness || 0) * 1000);
  const mortarFraction = Number.isFinite(result?.mortarFraction) ? result.mortarFraction * 100 : null;
  const wallTies = result?.wallTies || {};
  const wallTieDelta = Number.isFinite(result?.wallTieDelta)
    ? result.wallTieDelta
    : Number.isFinite(wallTies.delta_u_W_m2K)
      ? wallTies.delta_u_W_m2K
      : null;
  const uUnadjusted = Number.isFinite(result?.uValueUnadjusted) ? result.uValueUnadjusted : result?.uValue;
  const uMortar = Number.isFinite(result?.uValueMortar) ? result.uValueMortar : result?.uValue;

  const renderLayers = (layers = []) => (
    <View style={styles.grid}>
      <View style={{ ...styles.gridCol, borderLeft: "none" }}>
        <Text style={styles.gridHeader}>Material</Text>
        {layers.map((layer) => (
          <Text key={`${layer.materialName}-${layer.thickness}`} style={styles.gridRow}>
            {layer.materialName}
          </Text>
        ))}
      </View>
      <View style={styles.gridCol}>
        <Text style={styles.gridHeader}>Thickness</Text>
        {layers.map((layer) => (
          <Text key={`${layer.materialName}-${layer.thickness}-thk`} style={styles.gridRow}>
            {formatThickness(layer.thickness)}
          </Text>
        ))}
      </View>
      <View style={styles.gridCol}>
        <Text style={styles.gridHeader}>λ (W/m·K)</Text>
        {layers.map((layer) => (
          <Text key={`${layer.materialName}-${layer.thickness}-lambda`} style={styles.gridRow}>
            {formatNumber(layer.lambda)}
          </Text>
        ))}
      </View>
    </View>
  );

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <Text style={styles.title}>U-value calculator</Text>
          <Text style={styles.subtitle}>Report generated {formatDateTime(generatedAt)}</Text>
        </View>

        {project && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Project</Text>
            <View style={styles.row}>
              <Text style={styles.label}>Name</Text>
              <Text style={styles.value}>{project.name || "—"}</Text>
            </View>
            <View style={styles.row}>
              <Text style={styles.label}>Location</Text>
              <Text style={styles.value}>{project.postcode || "—"}</Text>
            </View>
            {project.building_use && (
              <View style={styles.row}>
                <Text style={styles.label}>Building use</Text>
                <Text style={styles.value}>{project.building_use}</Text>
              </View>
            )}
          </View>
        )}

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Summary</Text>
          <View style={styles.row}>
            <Text style={styles.label}>Component type</Text>
            <Text style={styles.value}>{componentType}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Final corrected U-value (W/m²K)</Text>
            <Text style={styles.value}>{formatNumber(result?.uValue)}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Unadjusted U-value (W/m²K)</Text>
            <Text style={styles.value}>{formatNumber(uUnadjusted)}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>U-value with mortar bridging (W/m²K)</Text>
            <Text style={styles.value}>{formatNumber(uMortar)}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Wall tie correction (W/m²K)</Text>
            <Text style={styles.value}>{formatNumber(wallTieDelta)}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Total thickness</Text>
            <Text style={styles.value}>{Number.isFinite(thicknessMm) ? `${thicknessMm} mm` : "—"}</Text>
          </View>
          {target && (
            <View style={styles.row}>
              <Text style={styles.label}>Part L target</Text>
              <Text style={styles.value}>
                {target} W/m²K {" "}
                <Text style={styles.tag}>{compliant ? "Compliant" : "Not compliant"}</Text>
              </Text>
            </View>
          )}
          {airLayer && (
            <Text style={styles.muted}>
              Air layer: {airLayer.type} ({formatThickness(airLayer.thickness)})
            </Text>
          )}
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Corrections and assumptions</Text>
          <View style={styles.row}>
            <Text style={styles.label}>Mortar bridging fraction</Text>
            <Text style={styles.value}>{mortarFraction !== null ? `${formatNumber(mortarFraction, 1)} %` : "—"}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Mortar thermal conductivity</Text>
            <Text style={styles.value}>{formatNumber(result?.mortarLambda || result?.mortar_thermal_conductivity_W_mK || null)}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Wall tie density</Text>
            <Text style={styles.value}>{formatNumber(wallTies.density_per_m2)} ties/m²</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Wall tie diameter</Text>
            <Text style={styles.value}>{formatNumber(wallTies.diameter_mm, 3)} mm</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Wall tie thermal conductivity</Text>
            <Text style={styles.value}>{formatNumber(wallTies.lambda_W_mK)} W/mK</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Wall tie correction</Text>
            <Text style={styles.value}>{formatNumber(wallTieDelta)} W/m²K</Text>
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Outer leaf</Text>
          {outerLayers?.length ? renderLayers(outerLayers) : (
            <Text style={styles.muted}>No outer layers provided.</Text>
          )}
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Inner leaf</Text>
          {innerLayers?.length ? renderLayers(innerLayers) : (
            <Text style={styles.muted}>No inner layers provided.</Text>
          )}
        </View>

        <Text style={styles.note}>
          Values are indicative only. Verify the final U-value with a qualified professional before issue.
        </Text>
      </Page>
    </Document>
  );
}
